import { KeyboardEvent, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatsGroup } from '../../helpers/locationStatistics';
import { StatsDetailRow, StatsMetrics, StatsTableHeaders } from './StatsDetailRow';
import { StatsRow } from './StatsRow';

export type StatsLayout = 'rows' | 'cards' | 'tabs';
export const STATS_LAYOUTS: StatsLayout[] = ['rows', 'cards', 'tabs'];

export const StatsContener = ({ groups, layout = 'rows' }: { groups: StatsGroup[]; layout?: StatsLayout }) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState<string | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const prefix = useId();
    const toggle = (id: string) => setExpanded(current => current === id ? null : id);
    const active = groups[activeIndex] || groups[0];
    const selected = groups.find(group => group.id === expanded);
    if (!active) return null;

    const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
        const next = event.key === 'ArrowRight' ? (index + 1) % groups.length
            : event.key === 'ArrowLeft' ? (index + groups.length - 1) % groups.length
                : event.key === 'Home' ? 0 : event.key === 'End' ? groups.length - 1 : null;
        if (next === null) return;
        event.preventDefault();
        setActiveIndex(next);
        tabRefs.current[next]?.focus();
    };

    if (layout === 'cards') return <div className="stats-cards-layout">
        <div className="stats-cards">{groups.map(group => <article key={group.id} className={`stats-card stats-tone-${group.id}`}>
            <h3><button type="button" aria-expanded={expanded === group.id} aria-controls={`${prefix}-card-panel`}
                onClick={() => toggle(group.id)}>
                {t(`stats.group_${group.id}`)}<span className="stats-disclosure-icon" aria-hidden="true">{expanded === group.id ? '−' : '+'}</span>
            </button></h3>
            <StatsMetrics counts={group} />
        </article>)}</div>
        <div id={`${prefix}-card-panel`} hidden={!selected} className="stats-card-detail">
            {selected && <><h3>{t(`stats.group_${selected.id}`)}</h3><StatsDetailRow details={selected.categories} label={t(`stats.group_${selected.id}`)} /></>}
        </div>
    </div>;

    if (layout === 'tabs') return <div className="stats-tabs-layout">
        <div role="tablist" aria-label={t('stats.categories')} className="stats-tabs">
            {groups.map((group, index) => <button key={group.id} ref={element => { tabRefs.current[index] = element; }}
                role="tab" type="button" id={`${prefix}-tab-${group.id}`} aria-controls={`${prefix}-panel-${group.id}`}
                aria-selected={group.id === active.id} tabIndex={group.id === active.id ? 0 : -1}
                onClick={() => setActiveIndex(index)} onKeyDown={event => onTabKeyDown(event, index)}>
                {t(`stats.group_${group.id}`)}
            </button>)}
        </div>
        {groups.map(group => <div key={group.id} role="tabpanel" tabIndex={0} id={`${prefix}-panel-${group.id}`}
            aria-labelledby={`${prefix}-tab-${group.id}`} hidden={group.id !== active.id} className="stats-tab-panel">
            {group.id === active.id && <><StatsMetrics counts={group} /><StatsDetailRow details={group.categories} label={t(`stats.group_${group.id}`)} /></>}
        </div>)}
    </div>;

    return <div className="stats-table-scroll">
        <table className="stats-table stats-summary-table" tabIndex={0}>
            <caption className="sr-only">{t('stats.categories')}</caption>
            <StatsTableHeaders />
            <tbody>{groups.map(group => <StatsRow key={group.id} group={group} expanded={group.id === expanded} onToggle={() => toggle(group.id)} />)}</tbody>
        </table>
    </div>;
};
